import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { completeSlackInstall } from '@/api/organizations'
import { Loader2, CheckCircle2, XCircle } from 'lucide-react'

export default function SlackInstallCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { org_slug, isAuthenticated } = useAuthStore()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<string>('')

  useEffect(() => {
    const code = searchParams.get('code')
    const state = searchParams.get('state')
    const slackError = searchParams.get('error')

    if (slackError) {
      setStatus('error')
      setError(slackError === 'access_denied' ? 'Installation was cancelled' : `Slack error: ${slackError}`)
      return
    }

    if (!code || !state) {
      setStatus('error')
      setError('Missing OAuth parameters')
      return
    }

    if (!isAuthenticated) {
      setStatus('error')
      setError('You must be signed in to complete the installation')
      return
    }

    completeSlackInstall(code, state)
      .then(() => {
        setStatus('success')
        setTimeout(() => {
          navigate(org_slug ? `/${org_slug}/admin/settings` : '/')
        }, 1500)
      })
      .catch((err) => {
        setStatus('error')
        setError(err?.response?.data?.detail || 'Installation failed')
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full text-center space-y-4">
        {status === 'loading' && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-[#EC5B2C] mx-auto" />
            <h1 className="text-xl font-bold text-gray-900">Installing Slack app...</h1>
            <p className="text-sm text-gray-500">Connecting your workspace</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
            <h1 className="text-xl font-bold text-gray-900">Slack connected!</h1>
            <p className="text-sm text-gray-500">Redirecting to settings...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 text-red-600 mx-auto" />
            <h1 className="text-xl font-bold text-gray-900">Installation failed</h1>
            <p className="text-sm text-red-600">{error}</p>
            <button
              onClick={() => navigate(org_slug ? `/${org_slug}/admin/settings` : '/')}
              className="px-4 py-2 bg-[#EC5B2C] text-white rounded hover:bg-[#EA4913] text-sm font-medium"
            >
              Back to settings
            </button>
          </>
        )}
      </div>
    </div>
  )
}
